import { useState, useEffect } from "react";
import { isMobile } from 'react-device-detect';
import { Input, Typography, ConfigProvider, Form, Button, Flex, Checkbox, Select } from 'antd';
import {
    GoBackButton,
    successValidationStatus,
    errorValidationStatus,
    InputPhoneNumber,
    routes,
    phoneNumberIsMatch,
    SMSVerification,
    PassportInput,
    SuccessIcon
} from '../components';
import { getSpecialCharacters as getPasswordSpecialCharacters, passwordMinLength, checkPassword } from '../components/password';
import { registrationSteps, defaultRegistrationStep } from '../components/registration';
import { registration, userExists, getUserFilterSettings, saveUserFilterSettings, auth, sendSMSCode } from "../components/api";
import { getPageTitleTheme, getPageDescriptionTheme, getMobileRegistrationFormTheme } from '../styles/pages/registration';
import "../styles/registration.scss";
import "../styles/registration-mobile.scss";

const { Text, Title, Link } = Typography;

const getFormItemClassName = () => isMobile ? '' : 'registration__step__field';
const getFormButtonClassName = () => isMobile ? 'registration-mobile__step__button' : 'registration__step__button';

const PageTitle = ({ text }) => (
    <ConfigProvider theme={getPageTitleTheme()}>
        <Title>{text}</Title>
    </ConfigProvider>
);

const PageDescription = ({ text }) => (
    <ConfigProvider theme={getPageDescriptionTheme()}>
        <Text>{text}</Text>
    </ConfigProvider>
);

const RegistratingPhoneNumberStep = ({ onSendVerifyCodeButtonClicked, onGoBack }) => {
    const [form] = Form.useForm();

    const fieldNames = {
        phoneNumber: 'phoneNumber',
        password: 'password',
        repeatedPassword: 'repeatedPassword',
        userAgreementAgreed: 'userAgreementAgreed',
        personalDataProcessingAgreed: 'personalDataProcessingAgreed',
        auctionRulesAgreed: 'auctionRulesAgreed'
    }

    const phoneNumberErrorText = 'Номер телефона должен содержать 10 цифр';
    const [phoneNumberValidationStatus, setPhoneNumberValidationStatus] = useState(successValidationStatus);
    const [phoneNumberHelpText, setPhoneNumberHelpText] = useState(null);

    const passwordHelpText = `Как минимум ${passwordMinLength} символов: заглавные и строчные латинские буквы, цифры и спецсимволы (${(getPasswordSpecialCharacters().join(', '))})`;
    const [passwordValidationStatus, setPasswordValidationStatus] = useState(successValidationStatus);
    const [passwordFeedbackIconVisible, setPasswordFeedbackIconVisible] = useState(false);

    const repeatedPasswordErrorText = 'Пароли не совпадают';
    const [repeatedPasswordValidationStatus, setRepeatedPasswordValidationStatus] = useState(successValidationStatus);
    const [repeatedPasswordHelpText, setRepeatedPasswordHelpText] = useState();
    const [repeatedPasswordFeedbackIconVisible, setRepeatedPasswordFeedbackIconVisible] = useState(false);

    const userAgreementAgreedErrorText = 'Необходимо принять пользовательское соглашение';
    const [userAgreementAgreedValidationStatus, setUserAgreementAgreedValidationStatus] = useState(successValidationStatus);
    const [userAgreementAgreedHelpText, setUserAgreementAgreedHelpText] = useState();

    const personalDataProcessingAgreedErrorText = 'Необходимо принять соглашение на обработку персональных данных';
    const [personalDataProcessingAgreedValidationStatus, setPersonalDataProcessingAgreedValidationStatus] = useState(successValidationStatus);
    const [personalDataProcessingAgreedHelpText, setPersonalDataProcessingAgreedHelpText] = useState();

    const auctionRulesAgreedErrorText = 'Необходимо принять правила аукциона';
    const [auctionRulesAgreedValidationStatus, setAuctionRulesAgreedValidationStatus] = useState(successValidationStatus);
    const [auctionRulesAgreedHelpText, setAuctionRulesAgreedHelpText] = useState();

    // установка свойств по статусам валидации полей
    useEffect(() => {
        if (repeatedPasswordValidationStatus === errorValidationStatus) {
            setRepeatedPasswordHelpText(repeatedPasswordErrorText);
            setRepeatedPasswordFeedbackIconVisible(true);
        }
        else {
            setRepeatedPasswordHelpText();
            setRepeatedPasswordFeedbackIconVisible(false);
        }

        setPasswordFeedbackIconVisible(passwordValidationStatus === errorValidationStatus);

        setPhoneNumberHelpText(phoneNumberValidationStatus === errorValidationStatus ? phoneNumberErrorText : null);

        setUserAgreementAgreedHelpText(
            userAgreementAgreedValidationStatus === errorValidationStatus
                ? userAgreementAgreedErrorText
                : null
        );

        setPersonalDataProcessingAgreedHelpText(
            personalDataProcessingAgreedValidationStatus === errorValidationStatus
                ? personalDataProcessingAgreedErrorText
                : null
        );

        setAuctionRulesAgreedHelpText(
            auctionRulesAgreedValidationStatus === errorValidationStatus
                ? auctionRulesAgreedErrorText
                : null
        );
    }, [phoneNumberValidationStatus, passwordValidationStatus, repeatedPasswordValidationStatus, userAgreementAgreedValidationStatus, personalDataProcessingAgreedValidationStatus, auctionRulesAgreedValidationStatus]);

    const getRegistrationOtherCheckboxClassName = () => isMobile ? 'registration-mobile__step__registration__checkbox-other' : getFormItemClassName();

    const getFormatedPhone = (inputPhone) => `7${inputPhone.replace(/[^0-9+]/g, '')}`;

    const handleFormFinish = (fieldsValue) => {
        let hasError = false;

        if (fieldsValue[fieldNames.phoneNumber] && phoneNumberIsMatch(fieldsValue[fieldNames.phoneNumber])) {
            setPhoneNumberValidationStatus(successValidationStatus);
        }
        else {
            hasError = true;
            setPhoneNumberValidationStatus(errorValidationStatus);
        }

        if (checkPassword(fieldsValue[fieldNames.password])) {
            setPasswordValidationStatus(successValidationStatus);
        }
        else {
            hasError = true;
            setPasswordValidationStatus(errorValidationStatus);
        }

        if (fieldsValue[fieldNames.repeatedPassword] && fieldsValue[fieldNames.repeatedPassword] === fieldsValue[fieldNames.password]) {
            setRepeatedPasswordValidationStatus(successValidationStatus);
        }
        else {
            hasError = true;
            setRepeatedPasswordValidationStatus(errorValidationStatus);
        }

        if (fieldsValue[fieldNames.userAgreementAgreed]) {
            setUserAgreementAgreedValidationStatus(successValidationStatus);
        }
        else {
            hasError = true;
            setUserAgreementAgreedValidationStatus(errorValidationStatus);
        }

        if (fieldsValue[fieldNames.personalDataProcessingAgreed]) {
            setPersonalDataProcessingAgreedValidationStatus(successValidationStatus);
        }
        else {
            hasError = true;
            setPersonalDataProcessingAgreedValidationStatus(errorValidationStatus);
        }

        if (fieldsValue[fieldNames.auctionRulesAgreed]) {
            setAuctionRulesAgreedValidationStatus(successValidationStatus);
        }
        else {
            hasError = true;
            setAuctionRulesAgreedValidationStatus(errorValidationStatus);
        }

        if (hasError)
            return;

        const formatedPhoneNumber = getFormatedPhone(fieldsValue[fieldNames.phoneNumber]);

        onSendVerifyCodeButtonClicked(`+7 ${fieldsValue[fieldNames.phoneNumber]}`, formatedPhoneNumber, fieldsValue[fieldNames.password]);
    }

    const SendSMSButton = () => (
        <Form.Item>
            <Flex vertical align="stretch">
                <Button type="primary" htmlType="submit" size="large" className={getFormButtonClassName()}>Получить СМС с кодом</Button>
            </Flex>
        </Form.Item>
    );

    return (
        <>
            <PageTitle text="Регистрация номера" />

            <div className={isMobile ? 'registration-mobile__step__content' : 'registration__step__content'}>
                <ConfigProvider theme={isMobile ? getMobileRegistrationFormTheme() : null}>
                    <Form
                        form={form}
                        onFinish={handleFormFinish}
                        layout="vertical"
                    >
                        <Form.Item
                            name={fieldNames.phoneNumber}
                            validateStatus={phoneNumberValidationStatus}
                            help={phoneNumberHelpText}
                            label="Телефон"
                            className={isMobile ? 'registration-mobile__step__registration__first-field' : getFormItemClassName()}
                        >
                            <InputPhoneNumber size="large" />
                        </Form.Item>

                        <Form.Item
                            name={fieldNames.password}
                            label="Пароль"
                            hasFeedback={passwordFeedbackIconVisible}
                            validateStatus={passwordValidationStatus}
                            help={passwordHelpText}
                            className={isMobile ? 'registration-mobile__step__registration__password-field' : getFormItemClassName()}
                        >
                            <Input.Password
                                size="large"
                                placeholder="Задайте пароль"
                            />
                        </Form.Item>

                        <Form.Item
                            name={fieldNames.repeatedPassword}
                            label="Повтор пароля"
                            hasFeedback={repeatedPasswordFeedbackIconVisible}
                            validateStatus={repeatedPasswordValidationStatus}
                            help={repeatedPasswordHelpText}
                            className={isMobile ? 'registration-mobile__step__registration__repeat-password-field' : getFormItemClassName()}
                        >
                            <Input.Password
                                size="large"
                                placeholder="Повторите пароль"
                            />
                        </Form.Item>

                        <div className={isMobile ? 'registration-mobile__step__registration__checkbox-container' : ''}>
                            <Form.Item
                                name={fieldNames.userAgreementAgreed}
                                validateStatus={userAgreementAgreedValidationStatus}
                                help={userAgreementAgreedHelpText}
                                valuePropName="checked"
                                className={getFormItemClassName()}
                            >
                                <Checkbox>Я принимаю <Link>пользовательское соглашение</Link></Checkbox>
                            </Form.Item>

                            <Form.Item
                                name={fieldNames.personalDataProcessingAgreed}
                                validateStatus={personalDataProcessingAgreedValidationStatus}
                                help={personalDataProcessingAgreedHelpText}
                                valuePropName="checked"
                                className={getRegistrationOtherCheckboxClassName()}
                            >
                                <Checkbox>Я соглашаюсь на <Link>обработку персональных данных</Link></Checkbox>
                            </Form.Item>

                            <Form.Item
                                name={fieldNames.auctionRulesAgreed}
                                validateStatus={auctionRulesAgreedValidationStatus}
                                help={auctionRulesAgreedHelpText}
                                valuePropName="checked"
                                className={getRegistrationOtherCheckboxClassName()}
                            >
                                <Checkbox>Я принимаю <Link>правила аукциона</Link></Checkbox>
                            </Form.Item>
                        </div>

                        <SendSMSButton />
                    </Form>
                </ConfigProvider>
            </div>
        </>
    );
}

const InputVerifyCodeStep = ({ inputedPhoneNumber, formatedPhoneNumber, onSMSVerified, onGoBack }) => (
    <SMSVerification
        pageTitleTheme={getPageTitleTheme()}
        pageDescriptionTheme={getPageDescriptionTheme()}
        phoneNumberRepresentation={inputedPhoneNumber}
        formatedPhoneNumber={formatedPhoneNumber}
        onRightInputedCode={onSMSVerified}
        buttonClassName={isMobile ? 'registration-mobile__step__button' : 'registration__step__button'}
    />
)

const FillingProfileStep = ({ onSubmiting, onGoBack }) => {
    const [form] = Form.useForm();

    const fieldNames = {
        fullName: 'fullName',
        email: 'email',
        passport: 'passport'
    }

    const handleFormFinish = (fieldsValue) => {
        const passportSerial = fieldsValue[fieldNames.passport];
        const passportNumber = fieldsValue[fieldNames.passport];;
        onSubmiting(fieldsValue[fieldNames.fullName], fieldsValue[fieldNames.email], passportSerial, passportNumber);
    }

    const ContinueButton = () => (
        <Form.Item>
            <Flex vertical align="stretch">
                <Button type="primary" htmlType="submit" size="large" className={getFormButtonClassName()}>Продолжить</Button>
            </Flex>
        </Form.Item>
    );

    return (
        <>
            <PageTitle text="Профиль" />

            <div className={isMobile ? 'registration-mobile__step__content' : 'registration__step__content'}>
                <Form
                    form={form}
                    onFinish={handleFormFinish}
                    layout="vertical"
                >
                    <Form.Item
                        name={fieldNames.fullName}
                        label="ФИО"
                        rules={[
                            {
                                required: true,
                                pattern: /[A-Za-zА-Яа-я]+ [A-Za-zА-Яа-я]+ [A-Za-zА-Яа-я]+/g,
                                message: 'Введите полностью Фамилию Имя Отчество'
                            }
                        ]}
                        className={getFormItemClassName()}
                    >
                        <Input size="large" placeholder="Иванов Иван Иванович" />
                    </Form.Item>

                    <Form.Item
                        name={fieldNames.email}
                        label="Эл. почта"
                        rules={[
                            {
                                required: true,
                                pattern: /.+@.+\..+/g,
                                message: 'Введите адрес электронной почты'
                            }
                        ]}
                        className={getFormItemClassName()}
                    >
                        <Input size="large" placeholder="Boravto@mail.ru" />
                    </Form.Item>

                    <Form.Item
                        name={fieldNames.passport}
                        label="Серия и номер паспорта"
                        rules={[
                            {
                                required: true,
                                pattern: /\d{2} \d{2} \d{6}/g,
                                message: 'Проверьте серию и номер паспорта, должно быть 10 цифр'
                            }
                        ]}
                        className={getFormItemClassName()}
                    >
                        <PassportInput size="large" />
                    </Form.Item>

                    <ContinueButton />
                </Form>
            </div>
        </>
    );
}

const SettingPreferences = ({ onSubmiting, onGoBack }) => {
    const [form] = Form.useForm();

    const fieldNames = {
        cities: 'cities',
        marks: 'marks',
        priceSegments: 'priceSegments'
    }

    const [selectedCities, setSelectedCities] = useState([]);

    const [selectedMarks, setSelectedMarks] = useState([]);

    const [selectedPriceSegments, setSelectedPriceSegments] = useState([]);

    const [marks, setMarks] = useState([]);
    const [cities, setCities] = useState([]);
    const [priceSegments, setPriceSegments] = useState([]);

    const allElement = { value: 0, label: 'Все' };

    const [isInit, setIsInit] = useState(true);

    useEffect(() => {
        if (isInit) {
            getUserFilterSettings().then(userFilters => {
                let allCities = getCollectionWithSelectAllElement(userFilters.cities);
                let allMarks = getCollectionWithSelectAllElement(userFilters.marks);
                let allPriceSegments = getCollectionWithSelectAllElement(userFilters.priceSegments);

                setCities(allCities);
                setMarks(allMarks);
                setPriceSegments(allPriceSegments);

                const defaultValue = [allElement.value];

                setSelectedCities(defaultValue);
                setSelectedMarks(defaultValue);
                setSelectedPriceSegments(defaultValue);

                form.setFieldValue(fieldNames.cities, defaultValue);
                form.setFieldValue(fieldNames.marks, defaultValue);
                form.setFieldValue(fieldNames.priceSegments, defaultValue);
            });

            setIsInit(false);
        }
    }, []);

    const getCollectionWithSelectAllElement = (collection) =>
        [
            allElement,
            ...collection.map(
                (element) => { return { value: element.id, label: element.name } }
            )
        ];

    const collectionsIsEqual = (collection1, collection2) => {
        let isEqual = true;

        if (collection1.length === collection2.length) {
            for (let index1 in collection1) {
                let value1 = collection1.at(index1)
                for (let index2 in collection2) {
                    if (value1 !== collection2.at(index2)) {
                        isEqual = false;
                        break;
                    }
                }

                if (!isEqual)
                    break;
            }
        }
        else {
            isEqual = false;
        }

        return isEqual;
    }

    const setSelectedValues = (newValues, allValues, selectedValues, selectedValuesAccessor, fieldName) => {
        console.log(newValues);

        if (newValues.length === 0) {
            newValues = [allElement.value];
        }
        else {
            let hasAllElementInNewValues = Array.from(newValues).some(value => value === allElement.value);
            if (hasAllElementInNewValues) {
                let hasAllElementInSelectedElements = selectedValues.some(value => value === allElement.value);
                if (hasAllElementInSelectedElements) {
                    let applingValues = Array.from(newValues).find(value => value !== allElement.value);
                    newValues = applingValues instanceof Array ? applingValues : [applingValues];
                }
                else {
                    newValues = [allElement.value];
                }
            }
        }

        if (collectionsIsEqual(newValues, selectedValues))
            return;

        selectedValuesAccessor(newValues);
        form.setFieldValue(fieldName, newValues);
    }

    const handleFormFinish = (fieldsValue) => {
        onSubmiting(fieldsValue[fieldNames.cities], fieldsValue[fieldNames.marks], fieldsValue[fieldNames.priceSegments]);
    }

    const onCitiesChanged = (value, options) => setSelectedValues(value, cities, selectedCities, setSelectedCities, fieldNames.cities);
    const onMarksChanged = (value, options) => setSelectedValues(value, marks, selectedMarks, setSelectedMarks, fieldNames.marks);
    const onPriceSegmentsChanged = (value, options) => setSelectedValues(value, priceSegments, selectedPriceSegments, setSelectedPriceSegments, fieldNames.priceSegments);

    return (
        <>
            <PageTitle text="Предпочтения" />

            <PageDescription text="Заполните поля, чтобы мы могли предложить Вам лучший выбор" />

            <Form
                form={form}
                onFinish={handleFormFinish}
                layout="vertical"
            >
                <Form.Item
                    name={fieldNames.cities}
                    label="Город выкупа"
                    className={getFormItemClassName()}
                >
                    <Select
                        size="large"
                        mode="multiple"
                        placeholder="Выберите город выкупа"
                        onChange={onCitiesChanged}
                        options={cities}
                    />
                </Form.Item>

                <Form.Item
                    name={fieldNames.marks}
                    label="Марка автомобиля"
                    className={getFormItemClassName()}
                >
                    <Select
                        size="large"
                        mode="multiple"
                        placeholder="Выберите марку"
                        onChange={onMarksChanged}
                        options={marks}
                    />
                </Form.Item>

                <Form.Item
                    name={fieldNames.priceSegments}
                    label="Цена"
                    className={getFormItemClassName()}
                >
                    <Select
                        size="large"
                        mode="multiple"
                        placeholder="Выберите диапазон цен"
                        onChange={onPriceSegmentsChanged}
                        options={priceSegments}
                    />
                </Form.Item>

                <Form.Item>
                    <Flex vertical align="stretch">
                        <Button type="primary" htmlType="submit" size="large" className={getFormButtonClassName()}>Завершить регистрацию</Button>
                    </Flex>
                </Form.Item>
            </Form>
        </>
    );
}

const SuccessStep = ({ enterButtonClicked }) => {
    return (
        <Flex vertical gap="20px" align="center" className={isMobile ? 'registration-mobile__step__success' : ''}>
            <SuccessIcon />

            <PageTitle text='Регистрация прошла успешно!' />
            <PageDescription text='Теперь Вы часть нашего сообщества' />

            <Flex vertical align="stretch">
                <Button className={getFormButtonClassName()} htmlType="button" type="primary" size="large" onClick={enterButtonClicked} >Войти</Button>
            </Flex>
        </Flex>
    );
}

export const Registration = () => {
    const [currentStep, setCurrentStep] = useState(defaultRegistrationStep);

    const registrationDataFields = {
        inputedPhoneNumber: 'inputedPhoneNumber',
        formatedPhoneNumber: 'formatedPhoneNumber',
        password: 'password',
        fullName: 'fullName',
        passportSeries: 'passportSeries',
        passportNumber: 'passportNumber'
    }
    const [registrationData, setRegistrationData] = useState();

    const stepsCountRepresentation = 3;

    const stepRepresentations = {
        registratingPhoneNumber: 1,
        fillingProfile: 2,
        settingPreferences: 3
    };
    const [currentStepRepresentation, setCurrentStepRepresentation] = useState(stepRepresentations.registratingPhoneNumber);

    useEffect(() => {
        if (currentStep === registrationSteps.registratingPhoneNumber) {
            setCurrentStepRepresentation(stepRepresentations.registratingPhoneNumber);

            setRegistrationData();
        }
        else if (currentStep === registrationSteps.verifyPhoneNumber) {
            setCurrentStepRepresentation(stepRepresentations.registratingPhoneNumber);

            sendSMSCode({ phone: registrationData[registrationDataFields.formatedPhoneNumber] }).then(
                () => { },
                (error) => { }
            );
        }

        else if (currentStep === registrationSteps.fillingProfile) {
            setCurrentStepRepresentation(stepRepresentations.fillingProfile);
        }

        else if (currentStep === registrationSteps.settingPreferences) {
            setRegistrationData();
            setCurrentStepRepresentation(stepRepresentations.settingPreferences);
        }
    }, [currentStep]);

    const goToAuthPage = () => window.location.href = routes.authorization;

    const goToNextStep = () => {
        if (currentStep === registrationSteps.registratingPhoneNumber)
            setCurrentStep(registrationSteps.verifyPhoneNumber);

        else if (currentStep === registrationSteps.verifyPhoneNumber)
            setCurrentStep(registrationSteps.fillingProfile);

        else if (currentStep === registrationSteps.fillingProfile)
            setCurrentStep(registrationSteps.settingPreferences);

        else if (currentStep === registrationSteps.settingPreferences)
            setCurrentStep(registrationSteps.completed);

        else if (currentStep === registrationSteps.completed)
            window.location.href = routes.root;
    }

    const goToPreviousStep = () => {
        if (currentStep === registrationSteps.registratingPhoneNumber)
            goToAuthPage();

        else if (currentStep === registrationSteps.verifyPhoneNumber
            || currentStep === registrationSteps.fillingProfile
        ) {
            setCurrentStep(registrationSteps.registratingPhoneNumber);
        }

        else if (currentStep === registrationSteps.settingPreferences)
            window.location.href = routes.root;
    }

    const handleSendVerifyCodeButtonClicked = (inputedPhoneNumber, formatedPhoneNumber, password) => {
        userExists({ phoneNumber: formatedPhoneNumber }).then(
            () => {
                // пользователь существует
            },
            (error) => {
                // пользователь с этим номером телефона не найден - продолжаем регистрацию
                if (error?.response && error.response?.status === 404) {
                    setRegistrationData(
                        {
                            [registrationDataFields.inputedPhoneNumber]: inputedPhoneNumber,
                            [registrationDataFields.formatedPhoneNumber]: formatedPhoneNumber,
                            [registrationDataFields.password]: password
                        }
                    );

                    goToNextStep();
                }
                else {
                    // ошибка отправки запроса, либо другая ошибка предоставляемая backend-ом
                }
            }
        );
    }

    const handleSubmitingProfile = (fullName, email, passportSerial, passportNumber) => {
        registration({
            fullName: fullName,
            phone: registrationData[registrationDataFields.formatedPhoneNumber],
            password: registrationData[registrationDataFields.password],
            email: email,
            passportSeries: passportSerial,
            passportNumber: passportNumber
        }).then(
            () => {
                auth(
                    registrationData[registrationDataFields.formatedPhoneNumber],
                    registrationData[registrationDataFields.password]
                ).then(
                    () => goToNextStep(),
                    (error) => { }
                );
            },
            (error) => { }
        );
    }

    const handleSubmitingPreferences = (selectedCities, selectedMarks, selectedPriceSegments) => {
        saveUserFilterSettings(
            {
                cities: selectedCities,
                marks: selectedMarks,
                priceSegments: selectedPriceSegments
            }
        ).then(
            () => goToNextStep(),
            (error) => { }
        )
    }

    const showOrRemoveGoBackButton = () => currentStep !== registrationSteps.completed
        && <GoBackButton onClick={goToPreviousStep} />;

    const showOrRemoveCurrentStepRepresentation = () => currentStep !== registrationSteps.verifyPhoneNumber
        && currentStep !== registrationSteps.completed
        && <Text
            className={
                isMobile
                    ? 'registration-mobile__navigation__step-representation'
                    : 'registration__navigation__step-representation'
            }
        >
            Шаг {currentStepRepresentation}/{stepsCountRepresentation}
        </Text>

    const Container = ({ children }) => (
        isMobile
            ?
            (
                <Flex vertical gap="20px" align="center" className="registration-mobile__main">
                    <Flex justify="space-between" className="registration-mobile__navigation">
                        {showOrRemoveGoBackButton()}
                        {showOrRemoveCurrentStepRepresentation()}
                    </Flex>
                    <Flex vertical justify="start" align="stretch" gap="24px" className="registration-mobile__step">
                        {children}
                    </Flex>
                </Flex>
            )
            :
            (
                <Flex vertical align="center" justify="center" className="registration__main">
                    <Flex vertical gap="small" align="center" justify="center" className="registration__interaction-window">
                        <Flex justify="space-between" className="registration__navigation">
                            {showOrRemoveGoBackButton()}
                            {showOrRemoveCurrentStepRepresentation()}
                        </Flex>
                        <Flex vertical justify="center" align="center" gap="24px" className="registration__step">
                            {children}
                        </Flex>
                    </Flex>
                </Flex>
            )
    );

    return (
        <Container>
            {currentStep === registrationSteps.registratingPhoneNumber &&
                <RegistratingPhoneNumberStep
                    onSendVerifyCodeButtonClicked={handleSendVerifyCodeButtonClicked}
                    onGoBack={goToPreviousStep}
                />
            }

            {currentStep === registrationSteps.verifyPhoneNumber &&
                <InputVerifyCodeStep
                    onSMSVerified={goToNextStep}
                    onGoBack={goToPreviousStep}
                    inputedPhoneNumber={registrationData[registrationDataFields.inputedPhoneNumber]}
                    formatedPhoneNumber={registrationData[registrationDataFields.formatedPhoneNumber]}
                />
            }

            {currentStep === registrationSteps.fillingProfile &&
                <FillingProfileStep
                    onSubmiting={handleSubmitingProfile}
                    onGoBack={goToPreviousStep}
                />
            }

            {currentStep === registrationSteps.settingPreferences &&
                <SettingPreferences
                    onSubmiting={handleSubmitingPreferences}
                    onGoBack={goToPreviousStep}
                />
            }

            {currentStep === registrationSteps.completed &&
                <SuccessStep
                    enterButtonClicked={goToNextStep}
                />
            }
        </Container>
    );
}